import { useRef, useState } from 'react'
import type { LayoutChangeEvent } from 'react-native'
import type { AutoScreenProps } from './screen.types'

// Represents the % of the screen taken up by content to change the view into a scroll view in react-native
const DEFAULT_SCROLL_THRESHOLD = 0.92

/**
 * Custom hook that handles the automatic enabling/disabling of scroll ability based on the content size and screen size.
 * @param {UseAutoPresetProps} props - The props for the `useAutoPreset` hook.
 * @returns {{boolean, Function, Function}} - The scroll state, and the `onContentSizeChange` and `onLayout` functions.
 */
export const useAutoPreset = (
	props: AutoScreenProps
): {
	scrollEnabled: boolean
	onContentSizeChange: (w: number, h: number) => void
	onLayout: (e: LayoutChangeEvent) => void
} => {
	const { preset, scrollEnabledToggleThreshold } = props
	const { percent = DEFAULT_SCROLL_THRESHOLD, point = 0 } =
		scrollEnabledToggleThreshold || {}

	const scrollViewHeight = useRef<null | number>(null)
	const scrollViewContentHeight = useRef<null | number>(null)
	const [scrollEnabled, setScrollEnabled] = useState(true)

	function updateScrollState() {
		if (scrollViewHeight.current === null || scrollViewContentHeight.current === null)
			return

		// check whether content firee=> n then toggle scroll state according to it
		const contentFitsScreen = (() => {
			if (point) {
				return scrollViewContentHeight.current < scrollViewHeight.current - point
			}
			return scrollViewContentHeight.current < scrollViewHeight.current * percent
		})()

		// content is less than the size of the screen, so we can disable scrolling
		if (scrollEnabled && contentFitsScreen) setScrollEnabled(false)

		// content is greater than the size of the screen, so let's enable scrolling
		if (!scrollEnabled && !contentFitsScreen) setScrollEnabled(true)
	}

	/**
	 * @param {number} w - The width of the content.
	 * @param {number} h - The height of the content.
	 */
	function onContentSizeChange(w: number, h: number) {
		// update scroll-view content height
		scrollViewContentHeight.current = h
		updateScrollState()
	}

	/**
	 * @param {LayoutChangeEvent} e = The layout change event.
	 */
	function onLayout(e: LayoutChangeEvent) {
		const { height } = e.nativeEvent.layout
		// update scroll-view  height
		scrollViewHeight.current = height
		updateScrollState()
	}

	// update scroll state on every render
	if (preset === 'auto') updateScrollState()

	return {
		scrollEnabled: preset === 'auto' ? scrollEnabled : true,
		onContentSizeChange,
		onLayout,
	}
}
